import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { InlineNotification } from '@hologram-dimension/inline-notification';
import { Link } from '@hologram-dimension/link';
import { Loader } from '@hologram-dimension/loader';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { useBulkStateChangeCall, useBulkStateChangePreview } from 'api';
import { DateTime } from 'luxon';
import { unparse } from 'papaparse';
import {
  FC,
  KeyboardEventHandler,
  MouseEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type {
  HandlersBulkSimPauseResumeResponse,
  StructsModifiedSim,
} from 'types/__generated__/SimInventoryApi';
import type { HologramSimIdsMap, OnSubmit, SimInventoryBulkStateAction } from 'types/common';
import downloadCSV from 'utils/files/downloadCSV';
import { formatNumber } from 'utils/numbers';
import styles from './SimInventoryBulkUpdateState.module.css';

const isModifiedSim = (sim: StructsModifiedSim): sim is Required<StructsModifiedSim> =>
  !!sim?.hologramSimId;

interface BannerPropsShared {
  plural: boolean;
}

interface HelpPausePreviewProps extends BannerPropsShared {}

const HelpPausePreview: FC<HelpPausePreviewProps> = ({ plural }) => (
  <>
    <div>
      A <span className={styles.helpTextBold}>pause</span> command will be issued to our carrier
      partners for the selected {plural ? 'SIMs' : 'SIM'}. Paused SIMs will not be able to use
      cellular data or send or receive SMS messages.
    </div>
    <div>
      <span className={styles.helpTextBold}>Paused SIMs are still charged monthly</span> for us to
      retain access to cellular carriers and platform functionality. Learn more about paused SIMs
      and data plans in our{' '}
      <Link
        href="https://support.hologram.io/hc/en-us/articles/360035213874"
        target="_blank"
        type="inline"
      >
        billing FAQs
      </Link>
      .
    </div>
  </>
);

interface HelpPauseSuccessProps extends BannerPropsShared {}

const HelpPauseSuccess: FC<HelpPauseSuccessProps> = ({ plural }) => (
  <>
    <BannerNotification variant="note" prefixOverride="In progress">
      A <span className={styles.helpTextBold}>pause</span> command has been issued to our carrier
      partners for the selected {plural ? 'SIMs' : 'SIM'}.
    </BannerNotification>
    <div>
      You should see the status of your {plural ? 'devices' : 'device'} update within 10 minutes. In
      rare cases it may take up to 24 hours. If you have any questions, please reach out to{' '}
      <Link href="mailto:support@hologram.io" type="inline">
        support@hologram.io
      </Link>
      .
    </div>
    <div>
      <span className={styles.helpTextBold}>Paused SIMs are still charged monthly</span> for us to
      retain access to cellular carriers and platform functionality. Paused SIMs will not be able to
      use cellular data or send or receive SMS messages. Learn more about paused SIMs and data plans
      in our{' '}
      <Link
        href="https://support.hologram.io/hc/en-us/articles/360035213874"
        target="_blank"
        type="inline"
      >
        billing FAQs
      </Link>
      .
    </div>
  </>
);
interface HelpResumePreviewProps extends BannerPropsShared {}

const HelpResumePreview: FC<HelpResumePreviewProps> = ({ plural }) => (
  <div>
    Your {plural ? 'SIMs' : 'SIM'} will be ready for use on our network within 10 minutes. In rare
    cases it may take up to 24 hours.
  </div>
);
interface HelpPauseSuccessProps extends BannerPropsShared {}

const HelpResumeSuccess: FC<HelpPauseSuccessProps> = ({ plural }) => (
  <>
    <BannerNotification variant="note" prefixOverride="In progress">
      A <span className={styles.helpTextBold}>resume</span> command has been issued to our carrier
      partners for the selected {plural ? 'SIMs' : 'SIM'}.
    </BannerNotification>
    <div>
      Your {plural ? 'SIMs' : 'SIM'} will be ready for use on our network within 10 minutes. In rare
      cases it may take up to 24 hours. If you have any questions, please reach out to{' '}
      <Link href="mailto:support@hologram.io" type="inline">
        support@hologram.io
      </Link>
      .
    </div>
  </>
);

interface ProccessedResponse {
  succeededSims: Map<HologramSimId, StructsModifiedSim>;
  warnedSims: Map<HologramSimId, StructsModifiedSim>;
}

interface SimPreviewWarningsProps {
  response: ProccessedResponse;
  pastVerb: string;
}

const SimPreviewWarnings: FC<SimPreviewWarningsProps> = ({ response, pastVerb }) => {
  const [expanded, setExpanded] = useState(false);

  // TODO: Make the accordion control
  const toggleExpanded = useCallback<MouseEventHandler>(() => {
    setExpanded((prev) => !prev);
  }, []);

  const toggleExpandedKey = useCallback<KeyboardEventHandler>((e) => {
    if (!(e.key === 'Enter' || e.key === ' ')) {
      return;
    }
    setExpanded((prev) => !prev);
  }, []);

  if (response.warnedSims.size === 0) {
    return null;
  }

  return (
    <div className={styles.warnings}>
      <div
        className={styles.warningsHeader}
        onClick={toggleExpanded}
        onKeyUp={toggleExpandedKey}
        tabIndex={0}
        role="button"
      >
        <Icon
          name={expanded ? 'ChevronSingleSouth' : 'ChevronSingleEast'}
          title={expanded ? 'Collapse' : 'Expand'}
        />
        <InlineNotification variant="error" className={styles.warningsHeaderNotification}>
          {formatNumber(response.warnedSims.size)} {response.warnedSims.size === 1 ? 'SIM' : 'SIMs'}{' '}
          cannot be {pastVerb}.
        </InlineNotification>
      </div>
      {expanded && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>SIM ID</th>
                <th>Enabled profile ICCID</th>
              </tr>
            </thead>
            <tbody>
              {[...response.warnedSims.values()].map((sim) => (
                <tr key={sim.hologramSimId}>
                  <td>{sim.deviceName}</td>
                  <td>{sim.hologramSimId}</td>
                  <td>{sim.activeProfileIccid}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

interface SimPreviewSuccessesProps {
  response: ProccessedResponse;
  verb: string;
}

const SimPreviewSuccesses: FC<SimPreviewSuccessesProps> = ({ response, verb }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const onDownloadCSV = useCallback(() => {
    if (response.succeededSims.size === 0) {
      return;
    }

    setDownloading(true);

    // TODO: I was tired when I wrote this, make it actually go async
    window.setTimeout(() => {
      if (response.succeededSims.size === 0) {
        setDownloading(false);
        return;
      }

      // Generate the CSV
      const columns: Array<keyof StructsModifiedSim> = [
        'hologramSimId',
        'deviceName',
        'deviceId',
        'activeProfileIccid',
      ];

      const data: Array<StructsModifiedSim> = [
        {
          hologramSimId: 'SIM ID',
          deviceName: 'Name',
          deviceId: 'Device ID',
          activeProfileIccid: 'Enabled profile ICCID',
        } as any,
        ...response.succeededSims.values(),
      ];

      const csvContent = unparse(data, { columns, header: false });

      // Create the file
      const timestamp = DateTime.now().toFormat('yyyyLLdd-hhmmssSSS');
      setDownloadSuccess(
        downloadCSV({ data: csvContent, filename: `SIMs-${verb}-${timestamp}.csv` })
      );

      setDownloading(false);
    }, 0);
  }, [verb, response]);

  if (response.succeededSims.size === 0) {
    return null;
  }

  return (
    <div className={styles.successes}>
      <div>
        {formatNumber(response.succeededSims.size)} selected{' '}
        {response.succeededSims.size === 1 ? 'SIM' : 'SIMs'}
      </div>
      <Button
        loading={downloading}
        iconStart={downloadSuccess ? 'CheckmarkSingle' : 'DownloadTray'}
        variant="secondary"
        size="small"
        onClick={onDownloadCSV}
      >
        Download CSV
      </Button>
    </div>
  );
};

interface SimInventoryBulkUpdateStateProps {
  action: SimInventoryBulkStateAction;
  sims: HologramSimIdsMap;
  onProcessing: (value: boolean) => void;
  onDone: (sims?: Array<HologramSimId>) => void;
  onPreviewDone: (validSimCount: number) => void;
}

const SimInventoryBulkUpdateState = forwardRef<OnSubmit, SimInventoryBulkUpdateStateProps>(
  ({ action, sims, onProcessing, onDone, onPreviewDone }, ref) => {
    const organizationId = useOrganization();
    // Preview handling
    const {
      bulkStateResponse,
      isLoading: previewLoading,
      error: previewError,
    } = useBulkStateChangePreview({
      action,
      organizationId,
      sims,
    });

    // Commit handling
    // TODO: move to hook as preview
    const [actionResponse, setActionResponse] = useState<HandlersBulkSimPauseResumeResponse>();
    const [actionError, setActionError] = useState<string | undefined>();
    const bulkActionApiCall = useBulkStateChangeCall({ organizationId, action, preview: false });
    const simsToCommit = useMemo(
      () =>
        new Set(
          (bulkStateResponse.succeededSIMs
            ?.filter((value) => !!value.hologramSimId)
            ?.map((value) => value.hologramSimId) ?? []) as Array<HologramSimId>
        ),
      [bulkStateResponse]
    );

    useEffect(() => {
      if (
        !previewLoading &&
        !previewError &&
        bulkStateResponse.succeededSIMs &&
        bulkStateResponse.warningSIMs &&
        (bulkStateResponse.succeededSIMs?.length > 0 || bulkStateResponse.warningSIMs?.length > 0)
      ) {
        onPreviewDone(simsToCommit.size);
      }
    }, [bulkStateResponse, onPreviewDone, previewError, previewLoading, simsToCommit]);

    const commitStateChange = useCallback(async () => {
      if (simsToCommit.size === 0) {
        setActionError('No SIMs would change state.');
        return;
      }

      onProcessing(true);

      let result: HandlersBulkSimPauseResumeResponse | undefined;
      try {
        result = await bulkActionApiCall([...simsToCommit.keys()]);

        setActionResponse(result);
        onDone(result?.succeededSIMs?.filter(isModifiedSim).map((sim) => sim.hologramSimId));
      } catch (ex) {
        if (typeof ex === 'string') {
          setActionError(ex);
        } else {
          setActionError(`An error occurred: ${ex}`);
          // eslint-disable-next-line no-console
          console.error(ex);
        }
      }

      if (!result && actionError) {
        setActionResponse(undefined);
      }
      onProcessing(false);
    }, [actionError, bulkActionApiCall, simsToCommit, onDone, onProcessing]);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit: commitStateChange,
      }),
      [commitStateChange]
    );

    // Error and help text display
    const error = previewError || actionError;

    const onlyOneSIMPreviewed = sims.size === 1;
    const responseToDisplay = useMemo(() => {
      const newResponse = actionResponse ?? bulkStateResponse;

      const newSucceededSims = new Map<HologramSimId, StructsModifiedSim>();
      newResponse.succeededSIMs?.forEach((sim) => {
        if (sim.hologramSimId) {
          newSucceededSims.set(sim.hologramSimId, sim);
        }
      });

      const newWarnedSims = new Map<HologramSimId, StructsModifiedSim>();
      newResponse.warningSIMs?.forEach((warning) => {
        if (warning?.sims) {
          warning.sims.forEach((sim) => {
            if (sim.hologramSimId && !newSucceededSims.has(sim.hologramSimId)) {
              newWarnedSims.set(sim.hologramSimId, sim);
            }
          });
        }
      });

      return { succeededSims: newSucceededSims, warnedSims: newWarnedSims };
    }, [actionResponse, bulkStateResponse]);

    let content;
    let helpContent;
    let pastVerb;

    switch (action) {
      case 'pause':
        if (actionResponse) {
          helpContent = (
            <HelpPauseSuccess plural={(actionResponse.succeededSIMs?.length ?? 0) > 1} />
          );
        } else {
          helpContent = <HelpPausePreview plural={!onlyOneSIMPreviewed} />;
        }
        pastVerb = 'paused';
        break;
      case 'resume':
        if (actionResponse) {
          helpContent = (
            <HelpResumeSuccess plural={(actionResponse.succeededSIMs?.length ?? 0) > 1} />
          );
        } else {
          helpContent = <HelpResumePreview plural={!onlyOneSIMPreviewed} />;
        }
        pastVerb = 'resumed';
        break;
      default:
        helpContent = null;
        pastVerb = 'acted on';
    }

    if (previewLoading) {
      content = <Loader>{`Checking selected ${onlyOneSIMPreviewed ? 'SIM' : 'SIMs'}...`}</Loader>;
    } else {
      content = (
        <>
          {!actionResponse && (bulkStateResponse.succeededSIMs?.length ?? 0) === 0 ? (
            <PanelNotification className={styles.notification} variant="error">
              {sims.size === 1
                ? `The selected SIM cannot be ${pastVerb}`
                : `No selected SIMs can be ${pastVerb}.`}
            </PanelNotification>
          ) : (
            <div className={styles.helpText}>
              {error && <BannerNotification variant="error">{error}</BannerNotification>}
              {helpContent}
            </div>
          )}
          {!onlyOneSIMPreviewed && (
            <>
              <SimPreviewWarnings response={responseToDisplay} pastVerb={pastVerb} />
              <SimPreviewSuccesses
                response={responseToDisplay}
                verb={actionResponse ? pastVerb : action}
              />
            </>
          )}
        </>
      );
    }

    return <div className={styles.wrapper}>{content}</div>;
  }
);

export default SimInventoryBulkUpdateState;
